// Build a pedigree tree structure from flat members data
// Format expected by FamilyTree component
// Includes step-family: half-siblings shown as cousin-like groups

function sortByBirthDate(a, b) {
  if (!a.birthDate && !b.birthDate) return 0;
  if (!a.birthDate) return 1;
  if (!b.birthDate) return -1;
  return a.birthDate.localeCompare(b.birthDate);
}

function toTreePerson(m) {
  if (!m) return null;
  return {
    id: m.id,
    name: m.displayName || `${m.firstName}${m.middleName ? ' ' + m.middleName : ''} ${m.lastName}`,
    gender: (m.gender || '').toUpperCase(),
    isLiving: m.isLiving !== false,
    kinshipTerm: m.kinshipLabel || '',
    birthDate: m.birthDate || null,
  };
}

function findSpouse(members, map, personId) {
  const person = map[String(personId)];
  if (!person) return null;
  if (person.spouseId) return toTreePerson(map[String(person.spouseId)]);
  const spouseOf = members.find(m => String(m.spouseId) === String(personId));
  return spouseOf ? toTreePerson(spouseOf) : null;
}

function findChildren(members, personId) {
  return members
    .filter(m => String(m.motherId) === String(personId) || String(m.fatherId) === String(personId))
    .sort(sortByBirthDate)
    .map(toTreePerson);
}

function findSiblings(members, map, personId) {
  const me = map[String(personId)];
  if (!me) return [];
  const ids = new Set();
  members.forEach(m => {
    if (String(m.id) === String(personId)) return;
    if (me.motherId && String(m.motherId) === String(me.motherId) && me.motherId) ids.add(m.id);
    if (me.fatherId && String(m.fatherId) === String(me.fatherId) && me.fatherId) ids.add(m.id);
  });
  const result = [];
  ids.forEach(sid => {
    const sib = map[String(sid)];
    if (!sib) return;
    const sibPerson = toTreePerson(sib);
    sibPerson.spouse = findSpouse(members, map, sid);
    const children = findChildren(members, sid);
    if (children.length > 0) sibPerson.children = children;
    result.push(sibPerson);
  });
  result.sort(sortByBirthDate);
  return result;
}

function findParentSiblings(members, map, parentId) {
  const parent = map[String(parentId)];
  if (!parent) return [];
  const ids = new Set();
  members.forEach(m => {
    if (String(m.id) === String(parentId)) return;
    if (parent.motherId && String(m.motherId) === String(parent.motherId) && parent.motherId) ids.add(m.id);
    if (parent.fatherId && String(m.fatherId) === String(parent.fatherId) && parent.fatherId) ids.add(m.id);
  });
  const result = [];
  const seen = new Set();
  ids.forEach(sid => {
    if (seen.has(String(sid))) return;
    seen.add(String(sid));
    const sib = map[String(sid)];
    if (!sib) return;
    const sibPerson = toTreePerson(sib);
    sibPerson.spouse = findSpouse(members, map, sid);
    const children = findChildren(members, sid);
    if (children.length > 0) sibPerson.children = children;
    result.push(sibPerson);
  });
  result.sort(sortByBirthDate);
  return result;
}

export function buildPedigree(members, personId) {
  if (!members || !personId) return null;

  const map = {};
  members.forEach(m => { map[String(m.id)] = m; });

  const me = map[String(personId)];
  if (!me) return null;

  const root = toTreePerson(me);
  root.isRoot = true;
  root.spouse = findSpouse(members, map, personId);

  // Full siblings (same mother AND father)
  const fullSiblingIds = new Set();
  // Half-siblings from father's other partners
  const paternalHalfSiblings = [];
  // Half-siblings from mother's other partners
  const maternalHalfSiblings = [];

  members.forEach(m => {
    if (String(m.id) === String(personId)) return;
    const sameMother = me.motherId && String(m.motherId) === String(me.motherId);
    const sameFather = me.fatherId && String(m.fatherId) === String(me.fatherId);

    if (sameMother && sameFather) {
      fullSiblingIds.add(m.id);
    } else if (sameFather && !sameMother) {
      paternalHalfSiblings.push(m);
    } else if (sameMother && !sameFather) {
      maternalHalfSiblings.push(m);
    }
  });

  const rootChildren = findChildren(members, personId);

  const rootSiblings = [];
  fullSiblingIds.forEach(sid => {
    const sib = map[String(sid)];
    if (!sib) return;
    const sibPerson = toTreePerson(sib);
    sibPerson.spouse = findSpouse(members, map, sid);
    const children = findChildren(members, sid);
    if (children.length > 0) sibPerson.children = children;
    rootSiblings.push(sibPerson);
  });
  rootSiblings.sort(sortByBirthDate);

  const rootGeneration = [root, ...rootSiblings].sort(sortByBirthDate);

  // Parents
  const father = me.fatherId ? toTreePerson(map[String(me.fatherId)]) : null;
  const mother = me.motherId ? toTreePerson(map[String(me.motherId)]) : null;

  // Step-parents
  let stepFather = null;
  let stepMother = null;
  if (me.motherId) {
    const motherPerson = map[String(me.motherId)];
    if (motherPerson && motherPerson.spouseId && String(motherPerson.spouseId) !== String(me.fatherId)) {
      stepFather = toTreePerson(map[String(motherPerson.spouseId)]);
    }
  }
  if (me.fatherId) {
    const fatherPerson = map[String(me.fatherId)];
    if (fatherPerson && fatherPerson.spouseId && String(fatherPerson.spouseId) !== String(me.motherId)) {
      stepMother = toTreePerson(map[String(fatherPerson.spouseId)]);
    }
  }

  // Half-sibling groups as "rootSiblings"-like structures for the tree
  // Father's half-siblings group: shows father (or father+step-mother) with their children
  const paternalHalfGroup = null; // Will be handled in tree layout
  const maternalHalfGroup = null; // Will be handled in tree layout

  const fatherSiblings = findParentSiblings(members, map, me.fatherId);
  const motherSiblings = findParentSiblings(members, map, me.motherId);
  const stepFatherSiblings = stepFather ? findParentSiblings(members, map, stepFather.id) : [];
  const stepMotherSiblings = stepMother ? findParentSiblings(members, map, stepMother.id) : [];

  // Grandparents
  let paternalGrandfather = null, paternalGrandmother = null;
  let maternalGrandfather = null, maternalGrandmother = null;
  let stepPaternalGrandfather = null, stepPaternalGrandmother = null;
  let stepMaternalGrandfather = null, stepMaternalGrandmother = null;

  if (me.fatherId) {
    const fp = map[String(me.fatherId)];
    if (fp) {
      if (fp.fatherId) paternalGrandfather = toTreePerson(map[String(fp.fatherId)]);
      if (fp.motherId) paternalGrandmother = toTreePerson(map[String(fp.motherId)]);
    }
  }
  if (me.motherId) {
    const mp = map[String(me.motherId)];
    if (mp) {
      if (mp.fatherId) maternalGrandfather = toTreePerson(map[String(mp.fatherId)]);
      if (mp.motherId) maternalGrandmother = toTreePerson(map[String(mp.motherId)]);
    }
  }
  if (stepFather) {
    const sf = map[String(stepFather.id)];
    if (sf) {
      if (sf.fatherId) stepPaternalGrandfather = toTreePerson(map[String(sf.fatherId)]);
      if (sf.motherId) stepPaternalGrandmother = toTreePerson(map[String(sf.motherId)]);
    }
  }
  if (stepMother) {
    const sm = map[String(stepMother.id)];
    if (sm) {
      if (sm.fatherId) stepMaternalGrandfather = toTreePerson(map[String(sm.fatherId)]);
      if (sm.motherId) stepMaternalGrandmother = toTreePerson(map[String(sm.motherId)]);
    }
  }

  // Build half-sibling tree nodes
  const paternalHalfNodes = paternalHalfSiblings.sort(sortByBirthDate).map(m => {
    const p = toTreePerson(m);
    p.kinshipTerm = m.gender === 'male' ? 'Half-Brother' : 'Half-Sister';
    p.spouse = findSpouse(members, map, m.id);
    const children = findChildren(members, m.id);
    if (children.length > 0) p.children = children;
    return p;
  });

  const maternalHalfNodes = maternalHalfSiblings.sort(sortByBirthDate).map(m => {
    const p = toTreePerson(m);
    p.kinshipTerm = m.gender === 'male' ? 'Half-Brother' : 'Half-Sister';
    p.spouse = findSpouse(members, map, m.id);
    const children = findChildren(members, m.id);
    if (children.length > 0) p.children = children;
    return p;
  });

  return {
    root,
    rootChildren,
    rootSiblings,
    rootGeneration,
    parentGeneration: {
      father: father || { id: 'empty-father', name: 'Father', gender: 'MALE', isLiving: false, kinshipTerm: '' },
      mother: mother || { id: 'empty-mother', name: 'Mother', gender: 'FEMALE', isLiving: false, kinshipTerm: '' },
      fatherSiblings,
      motherSiblings,
      stepFather,
      stepMother,
      stepFatherSiblings,
      stepMotherSiblings,
      // Half-sibling data
      paternalHalfSiblings: paternalHalfNodes,
      maternalHalfSiblings: maternalHalfNodes,
    },
    grandparentGeneration: {
      paternalGrandfather: paternalGrandfather || { id: 'empty-pgf', name: 'Grandfather', gender: 'MALE', isLiving: false, kinshipTerm: '' },
      paternalGrandmother: paternalGrandmother || { id: 'empty-pgm', name: 'Grandmother', gender: 'FEMALE', isLiving: false, kinshipTerm: '' },
      maternalGrandfather: maternalGrandfather || { id: 'empty-mgf', name: 'Grandfather', gender: 'MALE', isLiving: false, kinshipTerm: '' },
      maternalGrandmother: maternalGrandmother || { id: 'empty-mgm', name: 'Grandmother', gender: 'FEMALE', isLiving: false, kinshipTerm: '' },
      stepPaternalGrandfather,
      stepPaternalGrandmother,
      stepMaternalGrandfather,
      stepMaternalGrandmother,
    },
  };
}